import { Alert, Button, Stack, TypographyProps } from "@mui/material";
import { useState } from "react";

import PageTabsV2 from "@/App/Layouts/PageBody/PageTabsV2";
import DetailedViewPageHeader from "@/App/Layouts/PageHeader/DetailedViewPageHeader";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppLink from "@/common/components/Link/AppLink";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { TenantRequestHelper } from "@/common/helpers/entity/tenantRequest";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  DataUpdatesHubClientMethodName,
  EntityType,
  TagEntityType,
  TenantRequestDto,
  TenantRequestStage,
} from "@/core/api/generated/v1";

import GeneralAttachedTagsDisplay from "../../General/GeneralTag/GeneralAttachedTagsDisplay";
import GeneralAttachedTagsOfEntityEditModal from "../../General/GeneralTag/GeneralAttachedTagsOfEntityEditModal";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import TenantRequestDeleteModal from "../TenantRequestDeleteModal";
import TenantRequestMenu from "../TenantRequestMenu";
import TenantRequestStagePrimaryActions from "../TenantRequestStagePrimaryActions";
import CommentsTabContent from "./Tabs/CommentsTabContent";
import DataGrantsTabContent from "./Tabs/DataGrantsTabContent";
import GeneralHistoryTabContent from "./Tabs/GeneralHistoryTabContent";
import OverviewTabContent from "./Tabs/OverviewTabContent";

export enum TenantRequestViewTabs {
  Overview = "Overview",
  DataGrants = "DataGrants",
  GeneralHistory = "GeneralHistory",
  Comments = "Comments",
}

const defaultHeaderProps = {
  withLink: false,
  typographyProps: undefined as Partial<TypographyProps> | undefined,
};

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  viewVariant: ViewVariant.Page,
};

export interface TenantRequestViewProps extends BaseEntityViewInheritableProps<TenantRequestDto> {
  tenantRequestId: string | null | undefined;
  tenantRequest: TenantRequestDto | null | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  headerProps?: Partial<typeof defaultHeaderProps>;
}

export default function TenantRequestView({
  tenantRequestId,
  tenantRequest,
  displayProps = defaultDisplayProps,
  headerProps = defaultHeaderProps,
  onUpdate,
  onDelete,
}: TenantRequestViewProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };
  headerProps = {
    ...defaultHeaderProps,
    ...headerProps,
  };

  const history = useAppHistory();
  const currentTenant = useCurrentTenant();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditTagsModalOpen, setIsEditTagsModalOpen] = useState(false);

  const tenantRequestRequest = useApiRequest(
    apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsTenantRequestIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      tenantRequestId: tenantRequestId!,
    },
    {
      deps: [tenantRequestId],
      skip: !tenantRequestId || !!tenantRequest,
    },
  );
  tenantRequest = tenantRequestRequest?.data || tenantRequest;

  useRealtimeDataUpdates<TenantRequestDto>({
    enabled: !!currentTenant?.id && !!tenantRequest?.id,
    debugKey: "TenantRequestView",
    channelNames: [
      DataUpdatesChannelName.Entity(null, EntityType.TenantRequest, tenantRequest?.id || ""),
      DataUpdatesChannelName.Entity(
        currentTenant?.id,
        EntityType.TenantRequest,
        tenantRequest?.id || "",
      ),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      tenantRequestRequest.handleEntityChanged2({
        dto: data,
        // cover issue when tags are null here as tenant-local data is not sent via SignalR
        getUpdatedEntity: (params) => ({
          ...params.oldEntity,
          ...params.newEntity,
          tags: params.newEntity.tags || params.oldEntity.tags,
        }),
      });
    },
  });

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb:
      (displayProps?.breadcrumbs &&
        tenantRequest && {
          idValue: tenantRequest.id!,
          newTitle: tenantRequest.globalNumber || "",
        }) ||
      undefined,
  });

  return (
    <BaseEntityView
      entityType={EntityType.TenantRequest}
      entityId={tenantRequestId}
      entity={tenantRequest}
      entityRequest={tenantRequestRequest}
    >
      <ViewLayout
        displayProps={displayProps}
        header={
          displayProps.header && (
            <DetailedViewPageHeader
              image={undefined}
              title={`Company request ${StringHelper.join(
                [tenantRequest?.globalNumber, tenantRequest?.externalNumber],
                " / ",
                { skipEmpty: true },
              )} `}
              subtitle={
                <Stack gap={0.5}>
                  {/* Tags */}
                  {TenantRequestHelper.isSentOrReceivedByMe(currentTenant, tenantRequest) &&
                    tenantRequest && (
                      <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
                        <GeneralAttachedTagsDisplay
                          tags={tenantRequest.tags}
                          defaultIsFolded={false}
                          edit={{
                            entityType: TagEntityType.TenantRequest,
                            entityId: tenantRequest.id,
                            onSaved: (newValue) => {
                              tenantRequestRequest.updateData((x) => {
                                x.tags = newValue || undefined;
                              });
                            },
                          }}
                        />
                      </AuthorizedElement>
                    )}

                  {/* Alerts */}
                  {TenantRequestHelper.isSentByMe(currentTenant, tenantRequest) &&
                    tenantRequest?.stageHistory?.stage === TenantRequestStage.Draft && (
                      <Alert severity='info'>
                        The request is on{" "}
                        <InlineApiEnumValue
                          type='TenantRequestStage'
                          value={tenantRequest?.stageHistory?.stage}
                          sx={{ fontWeight: "bold" }}
                        />{" "}
                        stage. You still can edit it. Complete and send it, so the receiver can see
                        it and respond. Once sent, you won&apos;t be able to edit the request.
                      </Alert>
                    )}
                  {TenantRequestHelper.isSentByMe(currentTenant, tenantRequest) &&
                    tenantRequest?.stageHistory?.stage === TenantRequestStage.Declined && (
                      <Alert severity='info'>
                        The sender <strong>declined</strong> this request. You can edit it and send
                        once again.
                      </Alert>
                    )}
                  {TenantRequestHelper.isReceivedByMe(currentTenant, tenantRequest) &&
                    tenantRequest?.stageHistory?.stage === TenantRequestStage.Declined && (
                      <Alert severity='info'>
                        You <strong>declined</strong> this request. The sender can update it and
                        send once again.
                      </Alert>
                    )}
                </Stack>
              }
              titleProps={{
                to: headerProps?.withLink
                  ? ROUTE_PATH.TENANT_REQUEST_VIEW(tenantRequest?.id)
                  : undefined,
                typographyProps: headerProps?.typographyProps,
              }}
              primaryActions={
                tenantRequest &&
                displayProps.actions && (
                  <Stack direction='row' spacing={1}>
                    {TenantRequestHelper.isSentByMe(currentTenant, tenantRequest) &&
                      TenantRequestHelper.canBeEdited(tenantRequest) && (
                        <AuthorizedElement permissions={[AppPermission.TenantRequestManage]}>
                          <Button
                            component={AppLink}
                            to={ROUTE_PATH.TENANT_REQUEST_EDIT(tenantRequest.id)}
                            variant='outlined'
                            color='text'
                            size='medium'
                            startIcon={<AppIcon of='edit' />}
                          >
                            Edit
                          </Button>
                        </AuthorizedElement>
                      )}

                    <TenantRequestStagePrimaryActions
                      variant='normal'
                      tenantRequest={tenantRequest}
                      onUpdate={(newValue) => {
                        tenantRequestRequest.replaceData(newValue);
                        onUpdate && onUpdate(newValue);
                      }}
                    />
                  </Stack>
                )
              }
              secondaryActions={
                tenantRequest &&
                displayProps.actions && (
                  <TenantRequestMenu
                    entity={tenantRequest}
                    displayProps={{
                      edit: true,
                      editTags: true,
                      delete: true,
                    }}
                    onUpdate={(newValue) => {
                      newValue
                        ? tenantRequestRequest.replaceData(newValue)
                        : tenantRequestRequest.refetch();
                      onUpdate && onUpdate(newValue);
                    }}
                    onDelete={() => (onDelete ? onDelete() : history.goBack())}
                  />
                )
              }
            />
          )
        }
      >
        <PageTabsV2
          tabIdsDefinition={TenantRequestViewTabs}
          defaultTabId={TenantRequestViewTabs.Overview}
          tabs={[
            { label: "Overview", value: TenantRequestViewTabs.Overview },
            {
              label: "Data grants",
              value: TenantRequestViewTabs.DataGrants,
              permissions: [AppPermission.DataGrantRead],
            },
            ...(TenantRequestHelper.isSentByMe(currentTenant, tenantRequest)
              ? [{ label: "History", value: TenantRequestViewTabs.GeneralHistory }]
              : []),
            { label: "Comments", value: TenantRequestViewTabs.Comments },
          ]}
        >
          {({ activeTabId: activeTab }) =>
            tenantRequest && (
              <>
                {activeTab === TenantRequestViewTabs.Overview && (
                  <OverviewTabContent
                    tenantRequest={tenantRequest}
                    onUpdate={(newValue) => tenantRequestRequest.replaceData(newValue)}
                  />
                )}
                {activeTab === TenantRequestViewTabs.DataGrants && (
                  <DataGrantsTabContent tenantRequest={tenantRequest} />
                )}
                {activeTab === TenantRequestViewTabs.GeneralHistory &&
                  TenantRequestHelper.isSentByMe(currentTenant, tenantRequest) && (
                    <GeneralHistoryTabContent tenantRequest={tenantRequest} />
                  )}
                {activeTab === TenantRequestViewTabs.Comments && (
                  <CommentsTabContent tenantRequest={tenantRequest} />
                )}
              </>
            )
          }
        </PageTabsV2>

        {/* Delete */}
        {tenantRequest && (
          <TenantRequestDeleteModal
            entity={tenantRequest}
            open={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onDelete={() => history.goBack()}
          />
        )}

        {/* Edit tags */}
        {tenantRequest && (
          <GeneralAttachedTagsOfEntityEditModal
            open={isEditTagsModalOpen}
            onClose={() => setIsEditTagsModalOpen(false)}
            entityType={TagEntityType.TenantRequest}
            entityId={tenantRequest.id!}
            onSaved={(newValue) => {
              tenantRequestRequest.updateData((x) => {
                x.tags = newValue || undefined;
              });
            }}
          />
        )}
      </ViewLayout>
    </BaseEntityView>
  );
}
